import { Switch } from '@headlessui/react';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { motion, Reorder, useDragControls } from 'framer-motion';
import { DragHandle } from '../../components_misc/DragHandle';
import { Toast } from '../../components_misc/Toast';
import { useSearchParams } from 'react-router-dom';
import { Result1 } from '../../types';
import { updateObjective } from '../../store/map3PendingSlice';
import { ResultItem3 } from '../dev/ResultItem3';
import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';
import { AddResultButton1 } from '../dev/AddResultButton1';
import { useConflictDetection } from '../../map_dependencies/hooks/useConflictDetection';
import { createResult1 } from '@mm-shared/types/Result1.utils';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import {
  isDividerEntry,
  dividerIso,
  walkDeadlines,
  useResultsOrderReconciliation,
} from './ResultsTab.helpers';

export function ResultsTab() {
  const [searchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const [showAllDates, setShowAllDates] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const controls = useDragControls();

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );
  if (!objective) {
    throw new Error(`Objective ${objectiveShortId} not found`);
  }

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // Get the milestone map to access its intervals
  const milestoneMap = useSelector((state: RootState) =>
    currentMap ? state.map3Data.milestoneMaps.find((map) => map.uuid === currentMap.uuid) : undefined,
  );

  // Get all possible intervals for the map
  const allPossibleIntervals = useMemo(() => {
    if (!milestoneMap) return [];
    return getIntervalsForMilestoneMap(milestoneMap);
  }, [milestoneMap]);

  const { resultsOrder, setResultsOrder } = useResultsOrderReconciliation({
    objective,
    allPossibleIntervals,
    showAllDates,
  });

  // Memoize a uuid → Result1 map for O(1) lookup in render + handleReorder
  const resultByUuid = useMemo(
    () => new Map(objective.results.map((r) => [r.uuid, r])),
    [objective.results],
  );

  const handleReorder = (newOrder: string[]) => {
    if (!canEdit) {
      console.warn('Reorder denied: user does not have edit permissions');
      return;
    }

    const previousOrder = resultsOrder;
    setResultsOrder(newOrder); // optimistic update

    // Walk the new order to derive deadlines for each result uuid
    const walked = walkDeadlines({
      newOrder,
      allPossibleIntervals,
      existingResults: objective.results,
    });

    const newResults: Result1[] = walked.map(({ uuid, resolvedDeadline }) => {
      const base = resultByUuid.get(uuid)!;
      return createResult1({
        ...base,
        deadline: resolvedDeadline,
      });
    });

    dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: { results: newResults },
      }),
    )
      .unwrap()
      .then(() => {
        // Clear any previous error on success
        setErrorMessage(null);
      })
      .catch((error) => {
        console.error('ResultsTab: dispatch failed, reverting order:', error);
        setResultsOrder(previousOrder);
        setErrorMessage('Failed to save reorder. Please try again.');
      });
  };

  // Add conflict detection - status updates are now handled in the hook
  useConflictDetection(currentMap?.uuid ?? '');

  const intervalName = milestoneMap?.interval
    ? milestoneMap.interval.charAt(0).toUpperCase() + milestoneMap.interval.slice(1)
    : '';

  // Derive the latest interval ending for AddResultButton1.
  // Walk the order in reverse to find the last divider sentinel.
  const latestIntervalEnding = useMemo(() => {
    for (let i = resultsOrder.length - 1; i >= 0; i--) {
      if (isDividerEntry(resultsOrder[i])) {
        return dividerIso(resultsOrder[i] as `divider:${string}`);
      }
    }
    return '';
  }, [resultsOrder]);

  return (
    <div>
      {/* Error toast — surfaces dispatch failures to the user */}
      <Toast
        message={errorMessage}
        variant="error"
        position="top-right"
        autoClose={true}
        autoCloseDelay={5000}
        onClose={() => setErrorMessage(null)}
      />

      <div className="flex justify-end mb-4">
        <div className="flex items-center">
          <Switch.Group>
            <div className="flex items-center">
              <Switch.Label className="mr-3 text-sm text-gray-600">
                Show All {intervalName}
              </Switch.Label>
              <Switch
                checked={showAllDates}
                onChange={setShowAllDates}
                className={`${
                  showAllDates ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    showAllDates ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
            </div>
          </Switch.Group>
        </div>
      </div>
      <div id="results">
        {/*
          DRAG MODEL: Reorder.Group values is a flat string[] of sentinel-tagged entries.
          RENDER MODEL: dividers are rendered as non-draggable Reorder.Items so they
          self-register via onLayoutMeasure, which populates Framer Motion's internal
          `order` array. Without this, updateOrder's values.indexOf filter silently
          drops unrendered values and deadlines compute incorrectly.
          See RESULTS_TAB.md for full rationale (option (e) from the TL review).
        */}
        <Reorder.Group axis="y" values={resultsOrder} onReorder={handleReorder}>
          {resultsOrder.map((entry) => {
            if (isDividerEntry(entry)) {
              const iso = dividerIso(entry);
              const formattedDate = new Date(iso).toLocaleDateString();
              return (
                // Non-draggable divider rendered as Reorder.Item so it registers
                // in Framer Motion's internal order via onLayoutMeasure.
                // dragListener={false} and drag={false} prevent user drag,
                // but layout={true} (default) ensures onLayoutMeasure fires.
                <Reorder.Item
                  key={`divider-${iso}`}
                  value={entry}
                  dragListener={false}
                  drag={false}
                >
                  <div className="pt-4 p-1 text-sm">{formattedDate}</div>
                </Reorder.Item>
              );
            }

            // Result entry (uuid)
            const result = resultByUuid.get(entry) as Result1 | undefined;
            if (!result) return null;

            return (
              <Reorder.Item
                key={`result-${entry}`}
                value={entry}
                className=""
                dragListener={canEdit}
              >
                <div className="flex relative rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1">
                  {canEdit ? (
                    <div
                      className="reorder-handle cursor-grab flex items-center"
                      onPointerDown={(e) => controls.start(e)}
                    >
                      <motion.div dragControls={controls} className="flex items-center">
                        <DragHandle className="h-6 w-6 p-1 stroke-gray-50 bg-slate-100" />
                      </motion.div>
                    </div>
                  ) : (
                    <div className="flex items-center w-6 h-6 p-1">
                      {/* Empty space to maintain layout alignment */}
                    </div>
                  )}
                  <ResultItem3
                    resultUuid={entry}
                    objectiveUuid={objective.uuid}
                    deadline={result.deadline ?? ''}
                    showAddButton={true}
                  />
                </div>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      </div>
      <AddResultButton1
        latestIntervalEnding={latestIntervalEnding}
        objectiveUuid={objective.uuid}
      />
    </div>
  );
}
